// Pure scoring for subdivision viability. 0–100 score with a breakdown
// of how each signal contributed, so the UI can explain WHY something
// ranks high.
//
// Inputs are optional where possible — a manually-added listing without
// SAPPA enrichment still gets a sensible, if partial, score.

import { SCORING_DEFAULTS } from './defaults.js';
import { minSitePerDwelling, minFrontage } from './zones.js';

/**
 * @param {Object} input
 * @param {Object} input.listing                      price, land_m2, frontage_m
 * @param {Object} [input.parcel]                     zone, is_corner, area_m2, frontage_m
 * @param {Object} [input.feaso]                      { profit_on_cost_pct }
 * @param {number} input.targetDwellings              1→N target
 * @param {Object} [input.config]                     SCORING_DEFAULTS-shaped
 */
export function scoreListing(input) {
  const cfg = { ...SCORING_DEFAULTS, ...(input.config ?? {}) };
  const { listing = {}, parcel, feaso, targetDwellings } = input;

  const breakdown = [];
  let score = 0;

  // 1. Corner block
  if (parcel?.is_corner === true) {
    score += cfg.corner_block_points;
    breakdown.push({ key: 'corner_block', label: 'Corner block', points: cfg.corner_block_points, met: true });
  } else if (parcel?.is_corner === false) {
    breakdown.push({ key: 'corner_block', label: 'Corner block', points: 0, met: false });
  } else {
    breakdown.push({ key: 'corner_block', label: 'Corner block', points: 0, met: null, note: 'SAPPA not fetched' });
  }

  // 2. Lot size meets zone minimum for target dwelling count.
  //    Prefer SAPPA's per-parcel overlay (layer 204) when present; fall back
  //    to the zone table. Overlays are authoritative where they apply.
  const overlays = parcel?.overlays ?? input.overlays ?? {};
  const land = parcel?.area_m2 ?? listing.land_m2 ?? null;
  const overlayPerDwelling = overlays.min_site_per_dwelling ?? null;
  const tablePerDwelling = parcel?.zone ? minSitePerDwelling(parcel.zone) : null;
  const perDwelling = overlayPerDwelling ?? tablePerDwelling;
  const perDwellingSource = overlayPerDwelling != null ? 'SAPPA overlay' : (tablePerDwelling != null ? 'zone table' : null);
  if (land != null && perDwelling != null) {
    const required = perDwelling * targetDwellings;
    const met = land >= required;
    if (met) score += cfg.lot_size_points;
    breakdown.push({
      key: 'lot_size',
      label: `Lot size ≥ ${required} m² (${perDwelling} × ${targetDwellings})`,
      points: met ? cfg.lot_size_points : 0,
      met,
      note: `actual ${Math.round(land)} m² · ${perDwellingSource}`,
    });
  } else {
    breakdown.push({
      key: 'lot_size',
      label: 'Lot size ≥ zone minimum',
      points: 0,
      met: null,
      note: !land ? 'no land area' : 'no zone',
    });
  }

  // 3. Frontage threshold. Prefer SAPPA's per-parcel frontage overlay
  //    (layer 205) when it applies — it reflects code requirements, not
  //    our generic 18m/22m rule of thumb.
  const frontage = parcel?.frontage_m ?? listing.frontage_m ?? null;
  const overlayFrontage = overlays.min_frontage ?? null;
  const frontageMin = overlayFrontage ?? minFrontage(targetDwellings);
  const frontageSource = overlayFrontage != null ? 'SAPPA overlay' : 'default';
  if (frontage != null) {
    const met = frontage >= frontageMin;
    if (met) score += cfg.frontage_points;
    breakdown.push({
      key: 'frontage',
      label: `Frontage ≥ ${frontageMin} m`,
      points: met ? cfg.frontage_points : 0,
      met,
      note: `actual ${frontage.toFixed(1)} m · ${frontageSource}`,
    });
  } else {
    breakdown.push({
      key: 'frontage',
      label: `Frontage ≥ ${frontageMin} m`,
      points: 0,
      met: null,
      note: 'unknown',
    });
  }

  // 4. Auto-feasibility — scaled between the threshold and the cap
  const poc = feaso?.profit_on_cost_pct ?? null;
  if (poc != null) {
    const { min_profit_on_cost_pct: floor, cap_profit_on_cost_pct: cap, profit_points: max } = cfg;
    let earned;
    if (poc < floor) earned = 0;
    else if (poc >= cap) earned = max;
    else earned = Math.round((poc - floor) / (cap - floor) * max);
    score += earned;
    breakdown.push({
      key: 'profit',
      label: `Profit on cost ≥ ${(floor * 100).toFixed(0)}%`,
      points: earned,
      met: earned > 0,
      note: `actual ${(poc * 100).toFixed(1)}%`,
    });
  } else {
    breakdown.push({
      key: 'profit',
      label: 'Auto-feasibility',
      points: 0,
      met: null,
      note: 'not computed',
    });
  }

  return {
    score: Math.round(score),
    maxScore:
      cfg.corner_block_points + cfg.lot_size_points +
      cfg.frontage_points + cfg.profit_points,
    breakdown,
  };
}
